import React, {
    useState,
} from "react";
import {
    cloneReport as cloneReportRequest,
    createReport,
    createReportFolder,
    deleteReport,
    deleteReportFolder,
    loadReportPlan,
    updateReport,
    updateReportFolder,
} from "../api/reportPlans";
import {
    reportToast,
} from "../../../../components/notifications/appToast";
import {
    applyReportPlanToPlan,
    clone,
    findFolder,
    findPlan,
    findReport,
    makeReport,
    nextId,
    reportFolderFromDto,
    reportFromDto,
} from "../reportConfigurationLogic";
import type {
    CloneReportConfigurationReportPayload,
    MainTab,
    PlanConfig,
    ReportConfig,
    ReportConfigurationState,
    ReportFolder,
} from "../types";

type SavedReportBaselines = Record<string, { plan: PlanConfig; folder: ReportFolder; report: ReportConfig; }>;

type ReportPlanManagementInputs = {
    data: ReportConfigurationState;
    setData: React.Dispatch<React.SetStateAction<ReportConfigurationState>>;
    selectedPlan: PlanConfig;
    selectedFolderId: string;
    selectedReportId: string;
    setSelectedPlanId: React.Dispatch<React.SetStateAction<string>>;
    setSelectedFolderId: React.Dispatch<React.SetStateAction<string>>;
    setSelectedReportId: React.Dispatch<React.SetStateAction<string>>;
    setExpandedFolders: React.Dispatch<React.SetStateAction<Set<string>>>;
    setMainTab: React.Dispatch<React.SetStateAction<MainTab>>;
    setSavedReportBaselines: React.Dispatch<React.SetStateAction<SavedReportBaselines>>;
    newFolderName: string;
    setNewFolderName: React.Dispatch<React.SetStateAction<string>>;
    newReportNames: Record<string, string>;
    setNewReportNames: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    setReportPlanError: React.Dispatch<React.SetStateAction<string>>;
    replaceSelectedPlan: (updater: (plan: PlanConfig) => PlanConfig) => void;
    errorMessage: (error: any, fallback: string) => any;
};

export function useReportPlanManagement({
    data,
    setData,
    selectedPlan,
    selectedFolderId,
    selectedReportId,
    setSelectedPlanId,
    setSelectedFolderId,
    setSelectedReportId,
    setExpandedFolders,
    setMainTab,
    setSavedReportBaselines,
    newFolderName,
    setNewFolderName,
    newReportNames,
    setNewReportNames,
    setReportPlanError,
    replaceSelectedPlan,
    errorMessage,
}: ReportPlanManagementInputs) {
    const [cloneDialog, setCloneDialog] = useState<null | { planId: string; folderId: string; reportId: string }>(null);
    const [cloneLoading, setCloneLoading] = useState(false);
    const [cloneError, setCloneError] = useState("");
    const [cloneFoldersLoadingClientId, setCloneFoldersLoadingClientId] = useState<number | null>(null);
    const [cloneFolderLoadErrorClientId, setCloneFolderLoadErrorClientId] = useState<number | null>(null);
    const [cloneFolderLoadError, setCloneFolderLoadError] = useState("");

    const cloneSourcePlan = cloneDialog ? findPlan(data, cloneDialog.planId) : undefined;
    const cloneSourceFolder = cloneSourcePlan && cloneDialog ? findFolder(cloneSourcePlan, cloneDialog.folderId) : undefined;
    const cloneSourceReport = cloneDialog ? findReport(cloneSourceFolder, cloneDialog.reportId) : undefined;

    const openCloneReportDialog = (folder: ReportFolder, report: ReportConfig) => {
        setCloneDialog({ planId: selectedPlan.id, folderId: folder.id, reportId: report.id });
        setCloneError("");
        setCloneFolderLoadErrorClientId(null);
        setCloneFolderLoadError("");
    };

    const loadCloneTargetFolders = async (clientId: number) => {
        if (!clientId) return;

        const plan = data.plans.find((item) => item.clientId === clientId);
        if (!plan || plan.reportPlanId || plan.reportFolders.length) return;

        setCloneFoldersLoadingClientId(clientId);
        setCloneFolderLoadErrorClientId(null);
        setCloneFolderLoadError("");

        try {
            const response = await loadReportPlan(clientId);
            setData((current) => ({
                ...current,
                plans: current.plans.map((item) => item.clientId === clientId ? applyReportPlanToPlan(item, response) : item),
            }));
        } catch (error) {
            console.log(error);
            setCloneFolderLoadErrorClientId(clientId);
            setCloneFolderLoadError(reportToast.messageFromError(error, "Unable to load folders for the selected Health Plan."));
        } finally {
            setCloneFoldersLoadingClientId((current) => current === clientId ? null : current);
        }
    };

    const refreshReportPlanAfterClone = async (clientId: number, newReportId: number) => {
        const response = await loadReportPlan(clientId);
        const targetPlan = data.plans.find((plan) => plan.clientId === clientId);
        const targetPlanId = targetPlan?.id || String(clientId);
        const targetFolderDto = response.folders?.find((folder) => folder.reports?.some((report) => report.reportId === newReportId))
            || response.folders?.[0];
        const targetReportDto = targetFolderDto?.reports?.find((report) => report.reportId === newReportId)
            || targetFolderDto?.reports?.[0];
        const nextFolderId = targetFolderDto ? `report_folder_${targetFolderDto.reportFolderId}` : "";
        const nextReportId = targetReportDto ? `report_${targetReportDto.reportId}` : "";

        setData((current) => ({
            ...current,
            plans: current.plans.map((plan) => plan.id === targetPlanId ? applyReportPlanToPlan(plan, response) : plan),
        }));
        setSelectedPlanId(targetPlanId);
        setSelectedFolderId(nextFolderId);
        setSelectedReportId(nextReportId);
        setExpandedFolders(new Set(nextFolderId ? [nextFolderId] : []));
        setMainTab("reports");
    };

    const cloneReport = async (payload: CloneReportConfigurationReportPayload) => {
        if (!cloneSourceReport?.reportId || cloneLoading) return;

        setCloneLoading(true);
        setCloneError("");
        const toastId = reportToast.loading("Cloning report...", payload.newReportName);

        try {
            const result = await cloneReportRequest(cloneSourceReport.reportId, payload);
            await refreshReportPlanAfterClone(payload.newClientId, result.newReportId);
            setCloneDialog(null);
            reportToast.updateSuccess(toastId, "Report cloned", payload.newReportName);
        } catch (error) {
            console.log(error);
            const message = errorMessage(error, "Unable to clone the report.");
            setCloneError(message);
            reportToast.updateError(toastId, "Report was not cloned", message);
        } finally {
            setCloneLoading(false);
        }
    };

    const selectPlan = (planId: string) => {
        const plan = findPlan(data, planId);
        const folder = plan.reportFolders[0];
        const report = folder?.reports[0];
        setSelectedPlanId(plan.id);
        setSelectedFolderId(folder?.id || "");
        setSelectedReportId(report?.id || "");
        setExpandedFolders(new Set(folder ? [folder.id] : []));
        setMainTab("reports");
    };

    const addFolder = async () => {
        const label = newFolderName.trim();
        if (!label) {
            const message = "Enter a folder name before creating the folder.";
            setReportPlanError(message);
            reportToast.warning("Folder name required", message);
            return;
        }

        if (!selectedPlan.reportPlanId) {
            const message = "Report plan metadata has not loaded yet. Please wait before adding a folder.";
            setReportPlanError(message);
            reportToast.warning("Folder cannot be created yet", message);
            return;
        }

        const toastId = reportToast.loading("Creating folder...", label);
        try {
            const dto = await createReportFolder(selectedPlan.reportPlanId, label);
            const id = `report_folder_${dto.reportFolderId}`;
            replaceSelectedPlan((plan) => ({
                ...plan,
                reportFolders: [...plan.reportFolders, reportFolderFromDto(dto, plan.reportFolders)],
            }));
            setNewFolderName("");
            setSelectedFolderId(id);
            setSelectedReportId("");
            setExpandedFolders(new Set([id]));
            setReportPlanError("");
            reportToast.updateSuccess(toastId, "Folder created", dto.label || label);
        } catch (error) {
            console.log(error);
            const message = "Unable to add the report folder.";
            setReportPlanError(message);
            reportToast.updateError(toastId, "Folder was not created", reportToast.messageFromError(error, message));
        }
    };

    const deleteFolder = async (folderId: string) => {
        if (selectedPlan.reportFolders.length <= 1) {
            reportToast.warning("Folder cannot be deleted", "At least one report folder must remain for the selected health plan.");
            return false;
        }
        const folderToDelete = selectedPlan.reportFolders.find((folder) => folder.id === folderId);
        if (!folderToDelete) return false;
        if (folderToDelete.isBuiltIn) {
            const message = "Built-in report folders cannot be deleted.";
            setReportPlanError(message);
            reportToast.warning("Protected folder", message);
            return false;
        }

        const toastId = reportToast.loading("Deleting folder...", folderToDelete.label);
        try {
            if (folderToDelete.reportFolderId) {
                await deleteReportFolder(folderToDelete.reportFolderId);
            }

            replaceSelectedPlan((plan) => ({ ...plan, reportFolders: plan.reportFolders.filter((folder) => folder.id !== folderId) }));
            if (selectedFolderId === folderId) {
                const nextFolder = selectedPlan.reportFolders.find((folder) => folder.id !== folderId);
                setSelectedFolderId(nextFolder?.id || "");
                setSelectedReportId(nextFolder?.reports[0]?.id || "");
            }
            setReportPlanError("");
            reportToast.updateSuccess(toastId, "Folder deleted", folderToDelete.label);
            return true;
        } catch (error) {
            console.log(error);
            const message = "Unable to delete the report folder.";
            setReportPlanError(message);
            reportToast.updateError(toastId, "Folder was not deleted", reportToast.messageFromError(error, message));
            return false;
        }
    };

    const addReport = async (folderId: string) => {
        const label = (newReportNames[folderId] || "").trim();
        if (!label) {
            const message = "Enter a report name before creating the report.";
            setReportPlanError(message);
            reportToast.warning("Report name required", message);
            return;
        }

        const folder = selectedPlan.reportFolders.find((item) => item.id === folderId);
        if (!folder?.reportFolderId) {
            const message = "Report folder metadata has not loaded yet. Please wait before adding a report.";
            setReportPlanError(message);
            reportToast.warning("Report cannot be created yet", message);
            return;
        }

        const toastId = reportToast.loading("Creating report...", label);
        try {
            const dto = await createReport(folder.reportFolderId, label);
            const id = `report_${dto.reportId}`;
            const report = reportFromDto(dto, makeReport(id, dto.label, [], false));
            const nextPlan = {
                ...selectedPlan,
                reportFolders: selectedPlan.reportFolders.map((item) => item.id === folderId ? { ...item, reports: [...item.reports, report] } : item),
            };
            const nextFolder = nextPlan.reportFolders.find((item) => item.id === folderId) || folder;
            replaceSelectedPlan((plan) => ({
                ...plan,
                reportFolders: plan.reportFolders.map((item) => item.id === folderId ? { ...item, reports: [...item.reports, report] } : item),
            }));
            setSavedReportBaselines((current) => ({
                ...current,
                [id]: {
                    plan: clone(nextPlan),
                    folder: clone(nextFolder),
                    report: clone(report),
                },
            }));
            setNewReportNames((current) => ({ ...current, [folderId]: "" }));
            setSelectedFolderId(folderId);
            setSelectedReportId(id);
            setExpandedFolders(new Set([folderId]));
            setReportPlanError("");
            reportToast.updateSuccess(toastId, "Report created", dto.label || label);
        } catch (error) {
            console.log(error);
            const message = "Unable to add the report.";
            setReportPlanError(message);
            reportToast.updateError(toastId, "Report was not created", reportToast.messageFromError(error, message));
        }
    };

    const deleteReportHandler = async (folderId: string, reportId: string) => {
        const folder = selectedPlan.reportFolders.find((item) => item.id === folderId);
        const reportToDelete = folder?.reports.find((report) => report.id === reportId);
        if (!reportToDelete) return false;
        if (reportToDelete.isBuiltIn) {
            const message = "Built-in reports cannot be deleted.";
            setReportPlanError(message);
            reportToast.warning("Protected report", message);
            return false;
        }

        const toastId = reportToast.loading("Deleting report...", reportToDelete.label);
        try {
            if (reportToDelete.reportId) {
                await deleteReport(reportToDelete.reportId);
            }

            replaceSelectedPlan((plan) => ({
                ...plan,
                reportFolders: plan.reportFolders.map((item) => item.id === folderId ? { ...item, reports: item.reports.filter((report) => report.id !== reportId) } : item),
            }));
            if (selectedReportId === reportId) {
                const nextReport = folder?.reports.find((report) => report.id !== reportId);
                setSelectedReportId(nextReport?.id || "");
            }
            setReportPlanError("");
            reportToast.updateSuccess(toastId, "Report deleted", reportToDelete.label);
            return true;
        } catch (error) {
            console.log(error);
            const message = "Unable to delete the report.";
            setReportPlanError(message);
            reportToast.updateError(toastId, "Report was not deleted", reportToast.messageFromError(error, message));
            return false;
        }
    };

    const renameFolder = async (folderId: string, label: string) => {
        const folder = selectedPlan.reportFolders.find((item) => item.id === folderId);
        const normalizedLabel = label.trim() || "New Folder";
        replaceSelectedPlan((plan) => ({
            ...plan,
            reportFolders: plan.reportFolders.map((item) => item.id === folderId ? { ...item, label: normalizedLabel } : item),
        }));
        if (!folder?.reportFolderId) return;

        try {
            const dto = await updateReportFolder(folder.reportFolderId, normalizedLabel);
            replaceSelectedPlan((plan) => ({
                ...plan,
                reportFolders: plan.reportFolders.map((item) => item.id === folderId ? reportFolderFromDto(dto, plan.reportFolders) : item),
            }));
            setReportPlanError("");
            reportToast.success("Folder renamed", dto.label || normalizedLabel);
        } catch (error) {
            console.log(error);
            const message = "Unable to rename the report folder.";
            setReportPlanError(message);
            reportToast.error("Folder rename failed", reportToast.messageFromError(error, message));
        }
    };

    const renameReport = async (folderId: string, reportId: string, label: string) => {
        const folder = selectedPlan.reportFolders.find((item) => item.id === folderId);
        const report = folder?.reports.find((item) => item.id === reportId);
        const normalizedLabel = label.trim() || "New Report";
        replaceSelectedPlan((plan) => ({
            ...plan,
            reportFolders: plan.reportFolders.map((item) => item.id === folderId ? {
                ...item,
                reports: item.reports.map((currentReport) => currentReport.id === reportId ? { ...currentReport, label: normalizedLabel } : currentReport),
            } : item),
        }));
        if (!report?.reportId) return;

        try {
            const dto = await updateReport(report.reportId, normalizedLabel);
            replaceSelectedPlan((plan) => ({
                ...plan,
                reportFolders: plan.reportFolders.map((item) => item.id === folderId ? {
                    ...item,
                    reports: item.reports.map((currentReport) => currentReport.id === reportId ? reportFromDto(dto, currentReport) : currentReport),
                } : item),
            }));
            setReportPlanError("");
            reportToast.success("Report renamed", dto.label || normalizedLabel);
        } catch (error) {
            console.log(error);
            const message = "Unable to rename the report.";
            setReportPlanError(message);
            reportToast.error("Report rename failed", reportToast.messageFromError(error, message));
        }
    };

    return {
        cloneDialog,
        setCloneDialog,
        cloneLoading,
        cloneError,
        cloneFoldersLoadingClientId,
        cloneFolderLoadErrorClientId,
        cloneFolderLoadError,
        cloneSourcePlan,
        cloneSourceReport,
        openCloneReportDialog,
        loadCloneTargetFolders,
        cloneReport,
        selectPlan,
        addFolder,
        deleteFolder,
        addReport,
        deleteReport: deleteReportHandler,
        renameFolder,
        renameReport,
    };
}
